import { useMutation } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

export type TaxYearTier =
  | "starter"
  | "standard"
  | "pro_lite"
  | "trader"
  | "pro"
  // The rescue ladder: three fixed bands, all granting the same unlimited
  // "concierge" tax-year entitlement. The band rides in the SKU lookup key.
  | "concierge"
  | "concierge_complex"
  | "concierge_forensic";
export type TaxYearAddon = "dual_nationality";

export interface CreateTaxYearCheckoutInput {
  tax_year: number;
  jurisdiction_code: string;
  tier: TaxYearTier;
  addons?: TaxYearAddon[];
  dual_nationality_code?: string;
  currency?: string;
  success_url?: string;
  cancel_url?: string;
}

export interface CreateTaxYearCheckoutResponse {
  id: string;
  url: string;
}

interface UseTaxYearCheckoutOptions {
  onSuccess?: (data: CreateTaxYearCheckoutResponse) => void;
  onError?: (error: unknown) => void;
}

/**
 * Creates a Stripe Checkout Session for a per-tax-year SKU and returns the
 * Stripe-hosted URL. Caller is expected to redirect (window.location.assign)
 * or open in an in-app browser (iOS).
 */
export const useTaxYearCheckout = ({
  onSuccess,
  onError,
}: UseTaxYearCheckoutOptions = {}) => {
  const { getToken } = useAuth();

  const mutation = useMutation({
    mutationFn: async (
      input: CreateTaxYearCheckoutInput,
    ): Promise<CreateTaxYearCheckoutResponse> => {
      const token = await getToken();
      const response = await authorizedAxios(token).post(
        "/stripe/create-tax-year-checkout",
        input,
      );
      return response.data;
    },
    onSuccess: (data) => onSuccess?.(data),
    onError: (err) => {
      console.error("[useTaxYearCheckout] error:", err);
      onError?.(err);
    },
  });

  return {
    createCheckout: mutation.mutate,
    createCheckoutAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
  };
};
